/**
 * Dates from the API are UTC but arrive without a timezone marker
 * (for example "2026-09-11T05:07:31"), so the browser reads them as local time
 * and an action taken at 3:07pm shows as 05:07. N21.
 *
 * appendUtc() marks such a string as UTC so it is read correctly, and the
 * formatters render it in Australian Eastern time, so the CRM reads the same
 * whether the person is in Melbourne or offshore.
 */
const AEST = "Australia/Melbourne";

const appendUtc = (val: string): string => {
    const s = val.trim();
    // already carries Z or a +hh:mm / -hh:mm offset - leave it alone
    if (/[zZ]$/.test(s) || /[+-]\d{2}:?\d{2}$/.test(s)) return s;
    // only treat ISO-looking values as UTC; anything else is left to the browser
    return /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(s) ? `${s.replace(" ", "T")}Z` : s;
};

const toDate = (val: string | Date | null | undefined): Date | null => {
    if (!val) return null;
    const d = val instanceof Date ? val : new Date(appendUtc(String(val)));
    return isNaN(d.getTime()) ? null : d;
};

/** Date and time in Australian Eastern time, e.g. 11/09/2026, 15:07 */
export const formatDateTime = (val: string | Date | null | undefined, fallback = "-"): string => {
    const d = toDate(val);
    if (!d) return fallback;
    return d.toLocaleString("en-AU", {
        timeZone: AEST,
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit", hour12: false,
    });
};

/** Date only in Australian Eastern time, e.g. 11/09/2026 */
export const formatDate = (val: string | Date | null | undefined, fallback = "-"): string => {
    const d = toDate(val);
    if (!d) return fallback;
    return d.toLocaleDateString("en-AU", {
        timeZone: AEST, day: "2-digit", month: "2-digit", year: "numeric",
    });
};

export default formatDateTime;
